import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import { Sender } from 'services/conversations/types';
import { useQuery } from 'services/api/hooks';
import { sendMessageByUser } from 'services/conversations/api';
import { format } from 'date-fns';
import { ToastType, useToast } from 'services/toast';
import { CandidateDetails } from 'services/conversations/components/ConversationDetailsDrawer/CandidateDetails';
import { CandidateDocuments } from 'services/conversations/components/ConversationDetailsDrawer/CandidateDocuments';
import { ScreeningStage } from 'services/conversations/components/ConversationDetailsDrawer/ScreeningStage';
import { WorkflowResponses } from 'services/conversations/components/ConversationDetailsDrawer/WorkflowResponses';
import { UnansweredQuestions } from 'services/conversations/components/ConversationDetailsDrawer/UnansweredQuestions';
import { useFormik } from 'formik';

import { useDrawer } from 'services/drawer/hooks';
import { OpenEndedQuestionsAccordion } from 'services/conversations/components/ConversationDetailsDrawer/OpenEndedQuestionsAccordion';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import {
  Conversation,
  ConversationStatus,
  NeedsReviewReason
} from 'generated-graphql/types';
import {
  useRestartConversationMutation,
  useUpdateConversationMutation
} from 'generated-graphql/hooks/apollo-hooks';
import { ApplicationStage } from 'modules/conversations/types';
import { AlertCard } from 'components/AlertCard';
import { useConversationMessages } from 'modules/conversations/hooks/useConversationMessages';
import { getDifferentConversationOngoing } from 'services/conversations/api';
import { CandidateCalendarMeeting } from 'services/conversations/components/ConversationDetailsDrawer/CandidateCalendarMeeting';
import { MoreTimeRequestedAccordion } from 'services/conversations/components/ConversationDetailsDrawer/MoreTimeRequestedAccordion';
import { ConversationItem } from '../types/ConversationItem';
import { Channels } from 'generated-graphql/graphql';
import { ConversationMessagesList } from './ConversationMessagesList';
import { ConversationApplicationStage } from './ConversationApplicationStage';
import { MessagesListRef } from 'services/conversations/components/MessagesList';
import { conversationMessagingDetailsSx } from '../styles';

type Props = {
  conversation: ConversationItem;
  shouldHideApplicationStage: boolean;
  shouldHideCandidateDetails?: boolean;
  onUpdateConversationLocally: (conversation: ConversationItem) => void;
  shouldHideScreeningInProgress?: boolean;
};

export const ConversationMessagingDetails: React.FC<Props> = ({
  conversation,
  shouldHideApplicationStage,
  shouldHideCandidateDetails = false,
  onUpdateConversationLocally,
  shouldHideScreeningInProgress = false
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [flaggedMessageIds, setFlaggedMessageIds] = useState<string[]>([]);
  const messagesListRef = useRef<MessagesListRef>(null);

  const {
    data: differentConversationOngoing,
    isLoading: isDifferentConversationLoading
  } = useQuery<Conversation | undefined>(getDifferentConversationOngoing, {
    twilioConversationId: conversation?.twilioConversationId ?? '',
    conversationId: conversation?.id ?? ''
  });

  const isDifferentConversationOngoing = useMemo(() => {
    const channel =
      conversation.activeChannel || conversation.campaign?.channel;

    // For email channel, we don't need to check if there is a different conversation ongoing
    // because email conversations can be ongoing simultaneously with the same candidate
    if (channel === Channels.Email) return false;

    // If twilioConversationId is missing, we can't reliably check for other conversations
    // Querying with an empty string would falsely match unrelated conversations
    if (!conversation?.twilioConversationId) return false;

    if (isDifferentConversationLoading) return true;

    // If the found conversation is an Email conversation, it shouldn't block SMS/WhatsApp
    // since email conversations can run simultaneously with other channels
    if (differentConversationOngoing?.activeChannel === Channels.Email) {
      return false;
    }

    return !!differentConversationOngoing;
  }, [
    conversation,
    isDifferentConversationLoading,
    differentConversationOngoing
  ]);

  const {
    isLoading: isFetchingMessages,
    messagesList,
    addMessageToList
  } = useConversationMessages({
    conversationId: conversation.id
  });

  const [applicationStage, setApplicationStage] = useState<
    ApplicationStage | undefined
  >();
  const { showToast } = useToast();
  const { updateDrawerProps } = useDrawer();
  const { track } = useTracking();

  useEffect(() => {
    setApplicationStage(
      conversation?.applicationStage
        ? (conversation.applicationStage as ApplicationStage)
        : undefined
    );
  }, [conversation]);

  const [restartConversationMutation] = useRestartConversationMutation();
  const [updateConversationMutation, { loading }] =
    useUpdateConversationMutation();

  const messagesListToCopy = useMemo(() => {
    return messagesList
      .map((message) => {
        const sender =
          message.sender === Sender.AGENT
            ? 'You'
            : conversation?.participantPhoneNumber || '';

        const datetime =
          message.deliveredAt || message.updatedAt || message.createdAt;

        const dateTimeFormatted = format(new Date(datetime), 'EEEE h:mma');

        return `${sender} 
  ${dateTimeFormatted}
  ${message.body}`;
      })
      .join('\n\n');
  }, [messagesList, conversation]);

  const formik = useFormik({
    initialValues: {
      messageBody: ''
    },
    onSubmit: async (values) => {
      try {
        const { message } = await sendMessageByUser({
          messageBody: values.messageBody,
          conversationId: conversation.id
        });
        addMessageToList(message);
        formik.resetForm();
      } catch (e) {
        showToast({ message: 'Could not send message', type: ToastType.Error });
      }
    }
  });

  const copyConversation = () => {
    track(TrackingEventName.CampaignDashboardConversationCopyConversation);
    navigator.clipboard.writeText(messagesListToCopy);
    showToast({ message: 'Conversation copied', type: ToastType.Success });
  };

  const updateConversationHandler = async (type: 'agent' | 'user') => {
    setIsLoading(true);
    try {
      const isHumanTakeover = type === 'user' ? true : false;

      const { data } = await updateConversationMutation({
        variables: {
          input: {
            id: conversation.id,
            isHumanTakeover
          }
        }
      });

      if (data?.updateConversation) {
        onUpdateConversationLocally(
          data?.updateConversation as ConversationItem
        );
      }

      updateDrawerProps({
        preventClose: isHumanTakeover,
        confirmationModalProps: {
          title: 'Hand this conversation back to Popp?',
          confirmationText: 'Yes, hand back',
          cancelText: 'No, I want to stay in control',
          onConfirm: async () => {
            const { data } = await updateConversationMutation({
              variables: {
                input: {
                  id: conversation.id,
                  isHumanTakeover: false
                }
              }
            });

            onUpdateConversationLocally(
              data?.updateConversation as ConversationItem
            );
            updateDrawerProps({ preventClose: false });
          }
        }
      });
    } catch (e) {
      showToast({ message: 'Something went wrong', type: ToastType.Error });
    } finally {
      setIsLoading(false);
    }
  };

  const restartConversationHandler = async () => {
    setIsLoading(true);
    try {
      const { data } = await restartConversationMutation({
        variables: {
          input: {
            conversationId: conversation.id
          }
        }
      });
      onUpdateConversationLocally(
        data?.restartConversation as ConversationItem
      );
    } catch (e) {
      showToast({ message: 'Something went wrong', type: ToastType.Error });
    } finally {
      setIsLoading(false);
    }
  };

  const onApplicationStageChanged = async (newApplicationStage: string) => {
    try {
      const updateApplicationStage = newApplicationStage as ApplicationStage;
      const setToNull = newApplicationStage === ApplicationStage.NO_STAGE;

      await updateConversationMutation({
        variables: {
          input: {
            id: conversation.id,
            applicationStage: setToNull ? null : updateApplicationStage
          }
        }
      });
      setApplicationStage(
        setToNull ? undefined : (newApplicationStage as ApplicationStage)
      );
      showToast({
        title: 'Candidate status changed',
        type: ToastType.Success
      });
    } catch (e) {
      showToast({
        title: 'Status could not be changed',
        message: (
          <Typography variant="body2" mt={1}>
            Try changing the status again.{' '}
            <Link href="mailto:support@joinpopp.com?subject=Failed to update conversation">
              contact support
            </Link>{' '}
            if the issue persists.
          </Typography>
        ),
        type: ToastType.Error
      });
    }
  };

  const { status, isHumanTakeover } = conversation;

  // Popp Classic knockout/screening outcomes don't map to WFB conversations,
  // so for workflow-managed conversations we show the per-question responses
  // instead of the legacy screening + questions accordions.
  const isWorkflowConversation = !!conversation.workflowRunId;

  const handleQuestionsLoaded = useCallback((messageIds: string[]) => {
    setFlaggedMessageIds(messageIds);
  }, []);

  const handleScrollToMessage = useCallback((messageId: string) => {
    messagesListRef.current?.scrollToMessage(messageId);
  }, []);

  // MoreTimeRequested is handled by its own accordion above; don't double-signal here.
  const reasons = conversation.unresolvedNeedsReviewReasons ?? [];
  const onlyMoreTimeRequested =
    reasons.length > 0 &&
    reasons.every((r) => r === NeedsReviewReason.MoreTimeRequested);
  const needsReview = !!conversation.needsReview && !onlyMoreTimeRequested;

  const stageUpdates = conversation.applicationStageUpdates?.items ?? [];
  const lastStageUpdatedAt =
    stageUpdates[stageUpdates.length - 1]?.stageUpdatedAt;

  return (
    <Stack sx={conversationMessagingDetailsSx.layout}>
      <Box sx={conversationMessagingDetailsSx.pane}>
        {conversation.status === ConversationStatus.OptedOut && (
          <AlertCard title="Candidate opted out" type="error" />
        )}
        <ConversationMessagesList
          ref={messagesListRef}
          channel={
            conversation.activeChannel ?? conversation.campaign?.channel ?? null
          }
          isFetchingMessages={isFetchingMessages}
          messagesList={messagesList}
          conversation={conversation}
          isDifferentConversationOngoing={isDifferentConversationOngoing}
          isLoading={isLoading}
          formik={formik}
          onCopyConversation={copyConversation}
          onUpdateConversation={updateConversationHandler}
          onRestartConversation={restartConversationHandler}
          flaggedMessageIds={flaggedMessageIds}
        />
      </Box>
      <Box sx={conversationMessagingDetailsSx.rightColumn}>
        {!shouldHideApplicationStage && (
          <ConversationApplicationStage
            value={applicationStage}
            lastUpdatedAt={lastStageUpdatedAt}
            isLoading={loading}
            onChange={onApplicationStageChanged}
          />
        )}
        <Box sx={conversationMessagingDetailsSx.accordions}>
          <MoreTimeRequestedAccordion
            conversation={conversation}
            onUpdateConversationLocally={onUpdateConversationLocally}
          />
          <UnansweredQuestions
            conversationId={conversation.id}
            formik={formik}
            updateConversationHandler={updateConversationHandler}
            isDifferentConversationOngoing={isDifferentConversationOngoing}
            isHumanTakeOver={!!isHumanTakeover}
            conversationStatus={status}
            needsReview={needsReview}
            unresolvedNeedsReviewReasons={
              conversation.unresolvedNeedsReviewReasons
            }
            onScrollToMessage={handleScrollToMessage}
            onQuestionsLoaded={handleQuestionsLoaded}
            onNeedsReviewChange={(needsReview) => {
              onUpdateConversationLocally({
                ...conversation,
                needsReview
              });
            }}
          />
          {isWorkflowConversation ? (
            <WorkflowResponses
              questionOutcomes={conversation.questionOutcomes?.items ?? []}
            />
          ) : (
            <>
              {!shouldHideScreeningInProgress && (
                <ScreeningStage
                  status={status}
                  screeningQuestionsOutcome={
                    conversation.screeningQuestionsOutcome || []
                  }
                />
              )}

              {!!conversation.openQuestionOutcomes?.length && (
                <OpenEndedQuestionsAccordion
                  openQuestionOutcomes={conversation.openQuestionOutcomes}
                  preferredAnswersScore={conversation.preferredAnswersScore}
                />
              )}
            </>
          )}
          <CandidateCalendarMeeting conversation={conversation} />
          {!shouldHideCandidateDetails && (
            <CandidateDetails conversation={conversation} />
          )}
          <CandidateDocuments conversation={conversation} />
        </Box>
      </Box>
    </Stack>
  );
};
